import { ComponentProps, useMemo, useRef, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import locale from "@bonliva-crm/locale";
import { SingleSelect } from "@bonliva-crm/ui";
import { LegalPersonTypeWithNoOption } from "@bonliva-crm/graphql";
import { useUpdateEffect } from "@hooks";
import { ControllableCollapsibleFavFilter } from "./_base/controllable-collapsible-fav-filter";
import { SingleValueChip } from "./_select/single-value-chip";
import { FilterWithFavouriteProps } from "../utils/filters-record-types";

export type LegalPersonTypeFilterData = LegalPersonTypeWithNoOption;

type Option = { label: string; value: string };

export type LegalPersonTypeFilterProps = FilterWithFavouriteProps & {
  excludeNoOption?: boolean;
};

export const LegalPersonTypeFilter = (props: LegalPersonTypeFilterProps) => {
  const options = useMemo(() => {
    return Object.values(LegalPersonTypeWithNoOption)
      .filter((v) => !props.excludeNoOption || v !== LegalPersonTypeWithNoOption.NoOption)
      .toLabelValue((w) => locale.legalPersonTypeWithNoOption[w]);
  }, [props.excludeNoOption]);

  const selectRef = useRef(null);
  const { setValue, resetField } = useFormContext();
  const { field, fieldState } = useController({ name: props.name });
  const [expanded, setExpanded] = useState(fieldState.isDirty);
  const title = props.label ?? locale["Employment type"];
  useUpdateEffect(() => {
    if (expanded) {
      selectRef.current.focus();
    }
  }, [expanded]);

  const clear = () => resetField(props.name);

  const value = {
    label: options.find((option) => option.value === field?.value)?.label,
    value: field?.value
  };

  const commonProps: ComponentProps<typeof SingleSelect<Option>> = {
    options,
    name: props.name,
    value,
    onChange: (selected) => {
      setValue(props.name, selected.value, { shouldDirty: true });
    },
    hideSelectedOptions: false,
    controlShouldRenderValue: false,
    placeholder: title,
    isSearchable: false,
    maxMenuHeight: 600,
    showBadge: !!field?.value,
    optionType: "radio",
    isClearable: !!field?.value,
    onClear: () => resetField(props.name)
  };

  if (props.setFavorite) {
    return (
      <ControllableCollapsibleFavFilter
        title={title}
        isDirty={fieldState.isDirty}
        isClearable={fieldState.isDirty}
        isExpanded={expanded}
        onClear={clear}
        favorite={props.favorite}
        onSetFavorite={props.setFavorite}
        onExpand={() => setExpanded(!expanded)}
      >
        <SingleSelect<Option>
          {...commonProps}
          selectRef={selectRef}
          isClearable={false}
          controlShouldRenderValue
          openMenuOnFocus
          menuPortalTarget={document.body}
          showBadge={false}
          placeholder={locale.SelectValue}
          menuPlacement="auto"
          styleProps={{
            menu: { showBorder: true },
            valueContainer: { padding: 0 },
            menuPortal: { zIndex: 9999 }
          }}
          components={{
            SingleValue: (p) => (
              <SingleValueChip {...p} onDelete={() => resetField(props.name)} />
            )
          }}
        />
      </ControllableCollapsibleFavFilter>
    );
  } else {
    return (
      <SingleSelect<Option>
        {...commonProps}
        styleProps={{
          control: { showBorder: true },
          menu: { showBorder: true }
        }}
      />
    );
  }
};
